import React from "react";

import { formatPrice } from "@/src/utils/formatPrice";

import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import { dateColumn, statusColumn } from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { getOfferEditHref } from "../../../offers/tableParams/offerColumns";

import type { TFunction } from "i18next";

import type { UserPurchaseRow } from "./purchasesColumns.types";
import type { UserPurchase } from "../../../../api/StatsAPI.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";
import type { StatusToneItem } from "../../analyticsColumns.types";

interface UserPurchasesColumnsOptions {
  t: TFunction;
  projectSlug: string | undefined;
  offerAbility: boolean;
}

const STATUS_TONE_LIST: StatusToneItem[] = [
  { value: "unknown", tone: "neutral" },
  { value: "active", tone: "success" },
  { value: "expired", tone: "neutral" },
  { value: "incomplete", tone: "warning" },
  { value: "error", tone: "error" },
  { value: "incomplete_expired", tone: "error" },
  { value: "processing", tone: "warning" },
];

const PURCHASE_TYPE_LIST = ["tvod", "tvod_bundle", "svod", "gift"];

export function userPurchaseRow(
  purchase: UserPurchase,
  offerLabel: string | undefined,
): UserPurchaseRow {
  return {
    offer_uuid: purchase.offer_uuid,
    status: purchase.status,
    offer_label: offerLabel,
    type: purchase.type,
    price: purchase.price ?? 0,
    currency: purchase.currency ?? "",
    purchase_date: purchase.purchase_date,
  };
}

export function getUserPurchaseHref(
  row: UserPurchaseRow,
  projectSlug: string | undefined,
  offerAbility: boolean,
): string | undefined {
  return offerAbility && row.offer_label !== undefined
    ? getOfferEditHref(projectSlug, row.offer_uuid)
    : undefined;
}

export function userPurchasesColumns({
  t,
  projectSlug,
  offerAbility,
}: UserPurchasesColumnsOptions): DataTableColumnDef<UserPurchaseRow>[] {
  return [
    statusColumn<UserPurchaseRow>(
      "status",
      t("status"),
      STATUS_TONE_LIST.map(({ value, tone }) => ({
        value,
        label: t(`purchase_status.${value}`),
        tone,
      })),
    ),
    {
      accessorKey: "offer_label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("offer")} />
      ),
      meta: { searchable: true, label: t("offer") },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={getUserPurchaseHref(row.original, projectSlug, offerAbility)}
        >
          {row.original.offer_label ?? ""}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("type")} />
      ),
      enableSorting: false,
      meta: {
        label: t("type"),
        width: "13%",
        minWidth: 105,
        filterOptions: PURCHASE_TYPE_LIST.map((type) => ({
          value: type,
          label: t(`purchase_type.${type}`),
        })),
      },
      cell: ({ row }) => String(t(`purchase_type.${row.original.type}`)),
    },
    {
      // Le tri porte sur le montant, pas sur le texte « prix devise ».
      accessorKey: "price",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("price")} />
      ),
      meta: { label: t("price"), width: "13%", minWidth: 110 },
      cell: ({ row }) => formatPrice(row.original.price, row.original.currency),
    },
    dateColumn<UserPurchaseRow>("purchase_date", t("purchase_date"), {
      width: "13%",
      minWidth: 160,
    }),
  ];
}
